import { type ImageProps } from 'next/image';
import type { Ref } from 'react';
import { forwardRef, useEffect, useState } from 'react';

import type { ProductTypeOptions } from '@/types/product';

import { ImagePlaceholder } from '@/components/ImagePlaceholder';

import { NextImageMemo } from './NextImageMemo';

export interface NextImageProps extends ImageProps {
  [key: string]: any;
  onStateChange?: (state: 'loading' | 'error' | 'loaded') => void;
  originalImageDimensions?: { x: number; y: number };
  productType?: ProductTypeOptions;
  showFallbackPlaceholder?: boolean;
  showPlaceholderBackground?: boolean;
}

export const NextImage = forwardRef(
  (
    {
      onStateChange,
      productType,
      showFallbackPlaceholder,
      showPlaceholderBackground,
      ...props
    }: NextImageProps,
    ref: Ref<HTMLImageElement>,
  ) => {
    const [state, setState] = useState<'loading' | 'error' | 'loaded'>(
      'loading',
    );
    useEffect(() => {
      onStateChange?.(state);
    }, [state, onStateChange]);

    const { originalImageDimensions, ...rest } = props;

    return (
      <>
        {state === 'error' ? (
          showFallbackPlaceholder && (
            <ImagePlaceholder
              className={`${state} ${props.className}`}
              data-test-id='NextImageImagePlaceholder'
              productType={productType}
              withBackground={showPlaceholderBackground}
            />
          )
        ) : (
          <NextImageMemo
            ref={ref}
            className={`${state} ${props.className}`}
            originalImageDimensions={originalImageDimensions}
            setState={setState}
            {...rest}
            data-test-id='NextImageNextImageMemo'
          />
        )}
      </>
    );
  },
);

NextImage.displayName = 'NextImage';
